import { useParams, Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getProductByGtin, getWhatsAppUrl } from "@/data/products";
import { MessageCircle, ArrowLeft } from "lucide-react";

const LOGO_URL =
  "https://balance8.co.uk/cdn/shop/files/Logo_02_b2cedfc7-a338-4500-a9d9-bd23002b1cb6.png?v=1728339609";

export function ProductPage() {
  const { gtin } = useParams<{ gtin: string }>();
  const product = gtin ? getProductByGtin(gtin) : undefined;

  if (!product) {
    return (
      <div className="min-h-screen bg-[#fafafa] font-[Poppins] flex flex-col">
        <header className="pt-10 pb-4 flex justify-center px-6">
          <img src={LOGO_URL} alt="Balance8" className="h-28 w-auto" />
        </header>

        <main className="flex-1 flex flex-col items-center justify-center px-6">
          <h1 className="text-2xl font-light text-[#020912] mb-2">
            Product Not Found
          </h1>
          <p className="text-sm text-gray-400 mb-8">
            This QR code does not match any product.
          </p>
          <Link to="/">
            <Button variant="outline" className="rounded-xl">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Products
            </Button>
          </Link>
        </main>

        <footer className="py-6 text-center border-t border-gray-100">
          <p className="text-[10px] text-gray-300 tracking-widest uppercase">
            Redefining Complete Wellness
          </p>
        </footer>
      </div>
    );
  }

  const whatsappUrl = getWhatsAppUrl(product);

  return (
    <div className="min-h-screen bg-[#fafafa] font-[Poppins] flex flex-col">
      <header className="pt-10 pb-2 flex justify-center px-6">
        <img src={LOGO_URL} alt="Balance8" className="h-28 w-auto" />
      </header>

      <main className="flex-1 flex flex-col items-center px-6 pt-4 pb-12 max-w-lg mx-auto w-full">
        {/* Hero line */}
        <p className="text-[11px] uppercase tracking-[0.25em] text-gray-400 mb-3 text-center">
          Support Beyond The Product
        </p>

        {/* Product name */}
        <h1 className="text-2xl md:text-3xl font-medium text-[#020912] text-center leading-snug mb-4">
          {product.name}
        </h1>

        {/* Main copy */}
        <p className="text-sm text-gray-500 text-center leading-relaxed mb-10 max-w-sm">
          Welcome to the Balance8 family. Connect with us on WhatsApp for
          personalised product guidance, usage recommendations, and ongoing
          support designed to help you get the most out of your product.
        </p>

        {/* CTA Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full max-w-sm"
        >
          <Button className="w-full h-14 text-base bg-[#25D366] hover:bg-[#1faa52] text-white font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all rounded-xl">
            <MessageCircle className="h-5 w-5 mr-2 fill-current" />
            Start My Journey
          </Button>
        </a>

        {/* Trust text */}
        <p className="mt-4 text-[11px] text-gray-400 text-center">
          We typically reply within minutes
        </p>
      </main>

      <footer className="py-6 text-center border-t border-gray-100">
        <p className="text-[10px] text-gray-300 tracking-widest uppercase">
          Redefining Complete Wellness
        </p>
      </footer>
    </div>
  );
}
